import React from "react";
import dayjs from "../../config/dayjs";

import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";

import { ThemeProvider, createTheme } from "@mui/material/styles";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptLocale from "date-fns/locale/pt-BR";

const DEFAULT_TZ = "America/Sao_Paulo";

function getMinStartTimeForDate(startDateIso) {
  if (!startDateIso) return null;

  const nowSP = dayjs().tz(DEFAULT_TZ);
  const startDaySP = dayjs(startDateIso).tz(DEFAULT_TZ);

  const isToday = startDaySP.isSame(nowSP, "day");
  if (!isToday) return null;

  const minutes = nowSP.minute();
  const addHours = minutes >= 55 ? 2 : 1;

  return nowSP.add(addHours, "hour").startOf("hour");
}

function getLaterDay(firstDate, secondDate) {
  if (!firstDate) return secondDate;
  if (!secondDate) return firstDate;

  return firstDate.isAfter(secondDate, "day") ? firstDate : secondDate;
}

export function StepSchedule({
  data,
  updateData,
  setIsValid,
  showErrors,
  mode = "create",
}) {
  const {
    type = "",
    sendImmediately = false,
    startDate = null,
    startTime = null,
    endDate = null,
  } = data;

  const isSingleImmediate = type === "single" && sendImmediately;
  const requiresSchedule = !isSingleImmediate;
  const requiresEndDate = type === "recurring";
  const todaySP = React.useMemo(() => dayjs().tz(DEFAULT_TZ).startOf("day"), []);
  const startDaySP = React.useMemo(
    () => (startDate ? dayjs(startDate).tz(DEFAULT_TZ).startOf("day") : null),
    [startDate],
  );
  const hasCampaignStarted =
    mode === "edit" && startDaySP && !startDaySP.isAfter(todaySP, "day");
  const minEndDate = React.useMemo(() => {
    const fallback = mode === "edit" ? todaySP : dayjs().tz(DEFAULT_TZ);
    const startDay = startDate ? dayjs(startDate).tz(DEFAULT_TZ) : null;

    return getLaterDay(startDay, fallback);
  }, [mode, startDate, todaySP]);
  const isEndDateBeforeMin =
    requiresEndDate &&
    endDate &&
    minEndDate &&
    dayjs(endDate)
      .tz(DEFAULT_TZ)
      .startOf("day")
      .isBefore(minEndDate.tz(DEFAULT_TZ).startOf("day"), "day");

  /* =========================
     Tema DatePicker
  ========================= */
  const startDateTheme = React.useMemo(
    () =>
      createTheme({
        components: {
          MuiOutlinedInput: {
            styleOverrides: {
              root: { backgroundColor: "#FFF", borderRadius: "6px" },
              notchedOutline: { borderColor: "hsl(220, 20%, 88%)" },
            },
          },
          MuiTouchRipple: {
            styleOverrides: { root: { display: "none" } },
          },
        },
      }),
    [],
  );

  /* =========================
     Regras automáticas
  ========================= */

  // Se for single => endDate sempre igual startDate
  React.useEffect(() => {
    if (type === "single" && startDate && endDate !== startDate) {
      updateData({ endDate: startDate });
    }
  }, [type, startDate]);

  // minTime baseado na data
  const minStartTimeSP = React.useMemo(
    () => getMinStartTimeForDate(startDate),
    [startDate],
  );

  // Se startTime ficar inválido
  React.useEffect(() => {
    if (!startTime || !minStartTimeSP) return;

    const selectedSP = dayjs(startTime).tz(DEFAULT_TZ);
    if (selectedSP.isBefore(minStartTimeSP)) {
      updateData({ startTime: null });
    }
  }, [startTime, minStartTimeSP]);

  // Recorrência: endDate nunca menor que startDate
  React.useEffect(() => {
    if (type !== "recurring" || !startDate || !endDate) return;

    const s = dayjs(startDate).startOf("day");
    const e = dayjs(endDate).startOf("day");

    if (e.isBefore(s)) {
      updateData({ endDate: null });
    }
  }, [type, startDate, endDate]);

  /* =========================
     Validação
  ========================= */

  const isValid =
    Boolean(type) &&
    (isSingleImmediate ||
      (Boolean(startDate) &&
        Boolean(startTime) &&
        (!requiresEndDate || (Boolean(endDate) && !isEndDateBeforeMin))));

  React.useEffect(() => {
    setIsValid(isValid);
  }, [isValid]);

  const typeError = showErrors && !type;
  const startDateError = showErrors && requiresSchedule && !startDate;
  const startTimeError = showErrors && requiresSchedule && !startTime;
  const endDateError =
    (showErrors && requiresSchedule && requiresEndDate && !endDate) ||
    Boolean(isEndDateBeforeMin);

  /* =========================
     Render
  ========================= */

  return (
    <Box sx={{ width: "100%" }}>
      {/* Tipo */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontSize: 14, mb: 1 }}>Tipo de campanha</Typography>

        <RadioGroup
          value={type}
          onChange={(e) => {
            const nextType = e.target.value;

            if (nextType === "recurring") {
              updateData({
                type: nextType,
                sendImmediately: false, // regra centralizada aqui
              });
            } else {
              updateData({ type: nextType });
            }
          }}
        >
          <FormControlLabel
            value="single"
            control={<Radio />}
            label="Disparo único"
          />
          <FormControlLabel
            value="recurring"
            control={<Radio />}
            label="Recorrência"
          />
        </RadioGroup>

        {typeError && (
          <Typography color="error" fontSize={12}>
            Selecione o tipo da campanha
          </Typography>
        )}
      </Box>

      {/* Início */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontSize: 14, mb: 1 }}>Início da campanha</Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={type === "recurring" ? false : sendImmediately}
              disabled={type === "recurring" || hasCampaignStarted}
              onChange={(e) =>
                updateData({ sendImmediately: e.target.checked })
              }
            />
          }
          label="Envio imediato ao criar a campanha"
        />

        {type === "recurring" && (
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
            Em campanhas recorrentes, o envio deve ser agendado.
          </Typography>
        )}
      </Box>

      {/* Agendamento */}
      {requiresSchedule && (
        <ThemeProvider theme={startDateTheme}>
          <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
            <Box sx={{ width: 260 }}>
              <Typography sx={{ fontSize: 13, mb: 1 }}>
                Data de início
              </Typography>

              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <DesktopDatePicker
                  format="DD/MM/YYYY"
                  value={startDate ? dayjs(startDate) : null}
                  minDate={dayjs().tz(DEFAULT_TZ)}
                  disabled={Boolean(hasCampaignStarted)}
                  onChange={(newValue) =>
                    updateData({
                      startDate: newValue?.toISOString() || null,
                      startTime: null,
                    })
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: startDateError,
                      helperText: hasCampaignStarted
                        ? "A data de início não pode ser alterada após o início da campanha"
                        : startDateError
                          ? "Campo obrigatório"
                          : "",
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ width: 260 }}>
              <Typography sx={{ fontSize: 13, mb: 1 }}>
                Hora de início
              </Typography>

              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                locale={ptLocale}
              >
                <TimePicker
                  ampm={false}
                  minutesStep={60}
                  value={startTime ? dayjs(startTime).toDate() : null}
                  minTime={minStartTimeSP?.toDate() || null}
                  disabled={Boolean(hasCampaignStarted)}
                  onChange={(newValue) =>
                    updateData({
                      startTime: newValue
                        ? dayjs(newValue).toISOString()
                        : null,
                    })
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: startTimeError,
                      helperText: startTimeError ? "Campo obrigatório" : "",
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Stack>

          {requiresEndDate && (
            <Box sx={{ width: 260 }}>
              <Typography sx={{ fontSize: 13, mb: 1 }}>
                Data de término
              </Typography>

              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <DesktopDatePicker
                  format="DD/MM/YYYY"
                  value={endDate ? dayjs(endDate) : null}
                  minDate={minEndDate}
                  onChange={(newValue) =>
                    updateData({
                      endDate: newValue?.toISOString() || null,
                    })
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: endDateError,
                      helperText: isEndDateBeforeMin
                        ? mode === "edit"
                          ? "A data de término não pode ser anterior a hoje"
                          : "A data de término não pode ser anterior ao início"
                        : endDateError
                          ? "Campo obrigatório"
                          : "",
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          )}
        </ThemeProvider>
      )}
    </Box>
  );
}
